import React, { useEffect, useState, useRef } from 'react';
import appConfig from '../Config/appConfig';
import api from '../Api/api';
import localContent from '../localData/localContent.json';
import { createRequestContext, getJourneyId, getLaunchPropertiesFromLocation, getTokenDiagnostics, isErrorDetailsEnabled, trackTelemetry } from '../Utils/telemetry';

const url = appConfig.APIBaseURL;

// ─── Local mock: simulates the API for multi-screen content ───────────────────
const localScreens = Array.isArray(localContent) ? localContent : [localContent];

const loadLocalScreen = (index, setTitle, setSections, setIsLongContent, setSourceContentId) => {
  const screen = localScreens[Math.max(0, Math.min(index, localScreens.length - 1))];
  setTitle(screen.title || '');
  setSections(screen.sections || []);
  setIsLongContent(screen.isLongContent || false);
  setSourceContentId(screen.sourceContentId ?? null);
};

const logTaskDebug = (event, details = {}) => {
  console.log(`[SutterSync][useTask][${event}]`, details);
};

// Client-side/transport failures (no HTTP response reached) — safe to retry.
const isNetworkError = (error) =>
  !error.response && (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ECONNRESET' ||
    error.message === 'Network Error'
  );

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const MAX_RETRIES = 2;
// ─────────────────────────────────────────────────────────────────────────────
// mockData is only ever supplied explicitly by the /content test page (ContentEngine.js).
// Real flow (HomePage.js) never passes it, so mockData never derives from authToken here.
const useTask = (authToken, SSLaunch, mockData) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [sections, setSections] = useState([]);
  const [isLongContent, setIsLongContent] = useState(false);
  const [formData, setFormData] = useState({});
  
  
  const [priorDecisions, setPriorDecisions] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [flowSheetId, setFlowSheetId] = useState(null);
  const [error, setError] = useState(null);
  // Temporary debugging aid (gated by SHOW-ERROR-DETAILS-ENABLED) — full dump of the last task/network error.
  const [errorDetails, setErrorDetails] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [sourceContentId, setSourceContentId] = useState(null);
  const localScreenIndex = useRef(0);

  const updateFormData = (
    key,
    value,
    label = null,
    metric = null,
    metricFlowSheetId = null
  ) => {

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [key]: { value, label, metric, flowSheetId: metricFlowSheetId },
      };
      
      
      return newData;
    });
  };

  const handleTaskOperation = async (operation, params) => {
    const {
      taskRefId,
      patId,
      fhirPatId,
      taskInst,
      epicTaskId,
      actionType,
      clearKeys,
      metadata,
      flowSheetId: paramFlowSheetId,
      accessToken,
    } = params;

    logTaskDebug('OPERATION_CALLED', {
      operation,
      hasApiUrl: Boolean(url),
      hasStarted,
      hasParams: Boolean(params && Object.keys(params).length > 0),
      formKeys: Object.keys(formData || {}).length,
    });

    // Local development mode: no API URL — use localContent.json with full navigation
    
    if (!url) {
      logTaskDebug('LOCAL_MODE_OPERATION', {
        operation,
        currentScreenIndex: localScreenIndex.current,
        totalScreens: localScreens.length,
      });
      setLoading(true);
      await new Promise(r => setTimeout(r, 300));
      try {
        if (operation === 'start') {
          localScreenIndex.current = 0;
          loadLocalScreen(0, setTitle, setSections, setIsLongContent, setSourceContentId);
        } else if (operation === 'progress' || operation === 'next') {
          const next = localScreenIndex.current + 1;
          if (next < localScreens.length) {
            localScreenIndex.current = next;
            loadLocalScreen(next, setTitle, setSections, setIsLongContent, setSourceContentId);
          } else {
            setMessage('All screens complete (local preview).');
          }
        } else if (operation === 'back') {
          const prev = localScreenIndex.current - 1;
          if (prev >= 0) {
            localScreenIndex.current = prev;
            loadLocalScreen(prev, setTitle, setSections, setIsLongContent, setSourceContentId);
          }
        } else if (operation === 'complete' || operation === 'finish' || operation === 'submit') {
          const next = localScreenIndex.current + 1;
          if (next < localScreens.length) {
            localScreenIndex.current = next;
            loadLocalScreen(next, setTitle, setSections, setIsLongContent, setSourceContentId);
          } else {
            setMessage('Content complete (local preview).');
            setSections([]);
          }
        } else if (operation === 'summary') {
          setMessage('Summary (local preview).');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    const combinedMockData = (mockData && typeof mockData === 'object') ? mockData : {};

    // Prevent duplicate start operations
    if (operation === 'start' && hasStarted) {
      logTaskDebug('START_SKIPPED_DUPLICATE', {
        operation,
        taskRefId: taskRefId || authToken?.TASKREFID || null,
      });
      return;
    }

    const logMetadata = operation === 'complete';
    const updateEpic = operation === 'complete';
    const syncValue = authToken?.SYNC || authToken?.sync;

    const payload = {
      taskRefId: taskRefId || authToken?.TASKREFID,
      PATID: patId || authToken?.PATID,
      FHIRPATID: fhirPatId || authToken?.FHIRPATID,
      taskInst: taskInst || authToken?.taskInst,
      epicTaskId: epicTaskId || authToken?.taskID,
      ...(syncValue ? {
        SYNC: syncValue,
      } : {}),
      actionType,
      clearKeys,
      metadata,
      flowSheetId: paramFlowSheetId || flowSheetId,
      token: accessToken || authToken?.access_token,
      refreshToken: authToken?.refresh_token,
      FNAME: authToken?.FNAME,
      LNAME: authToken?.LNAME,
      PATIENTSEX: authToken?.PATIENTSEX,
      Program: authToken?.Program,
      logMetadata,
      updateEpic,
      WPRINTERNALID: authToken?.WPRINTERNALID || null,
      SSLaunch: SSLaunch || null,
      E8T2DStatus: authToken?.E8T2DStatus || null,
      E8sysCCV: authToken?.E8sysCCV || null,
      E8diaCCV: authToken?.E8diaCCV || null,
      E8sysBASE: authToken?.E8sysBASE || null,
      E8diaBASE: authToken?.E8diaBASE || null,
      TESTPATIENT: authToken?.TESTPATIENT || 'No',
      SWAPOUT: authToken?.SWAPOUT || null,
      ...(Object.keys(combinedMockData).length > 0 && {
      mockData: combinedMockData
      })
    };

    if (Object.keys(formData).length > 0) {
      // Clean formData before sending to prevent circular references
      const cleanFormData = {};
      Object.keys(formData).forEach(key => {
        const item = formData[key];
        if (item && typeof item === 'object' && item.value !== undefined) {
          // Handle array values (for input fields)
          let cleanValue = item.value;
          if (Array.isArray(item.value)) {
            cleanValue = item.value
              .filter(opt => opt !== '' && opt !== null && opt !== undefined) // strip stale empty strings
              .map(opt => {
                if (typeof opt === 'object' && opt !== null) {
                  return {
                    id: opt.id,
                    label: opt.label,
                    value: opt.value,
                    inputType: opt.inputType,
                    min: opt.min,
                    max: opt.max,
                    required: opt.required,
                    placeholder: opt.placeholder
                  };
                }
                return opt;
              });
          }
          
          cleanFormData[key] = {
            value: cleanValue,
            label: item.label || '',
            metric: item.metric || null,
            flowSheetId: item.flowSheetId || null
          };
        }
      });
      payload.formData = cleanFormData;
    }
    
    
    setLoading(true);
    // Declared outside try so the catch block can include them in the error dump.
    const endpoint = `${url}/api/v1/task/${operation}`;
    const journeyId = getJourneyId();
    const { requestId } = createRequestContext(operation, journeyId);
    try {
      logTaskDebug('REQUEST_PAYLOAD_SUMMARY', {
        operation,
        endpoint,
        requestId,
        journeyId,
        taskRefId: payload.taskRefId || null,
        hasFormData: Boolean(payload.formData),
        formDataKeys: payload.formData ? Object.keys(payload.formData).length : 0,
        actionType: payload.actionType || null,
        hasMetadata: Boolean(payload.metadata),
        hasMockData: Boolean(payload.mockData),
      });

      let response;
      for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
        console.log('[SutterSync][TaskRequest][START]', {
          operation,
          endpoint,
          taskRefId: payload.taskRefId || null,
          requestId,
          journeyId,
          attempt
        });
        trackTelemetry('SutterSync.Task.Start', {
          operation,
          endpoint,
          requestId,
          journeyId,
          taskRefId: payload.taskRefId || null,
          taskInst: payload.taskInst || null,
          epicTaskId: payload.epicTaskId || null,
          sync: payload.SYNC || null,
          attempt,
          ...getTokenDiagnostics(authToken),
          ...getLaunchPropertiesFromLocation(),
        });

        try {
          response = await api.post(endpoint, payload, {
            headers: {
              'x-request-id': requestId,
              'x-correlation-id': journeyId
            }
          });
          break;
        } catch (fetchErr) {
          if (attempt <= MAX_RETRIES && isNetworkError(fetchErr)) {
            logTaskDebug('RETRY', {
              operation,
              requestId,
              journeyId,
              taskRefId: payload.taskRefId || null,
              attempt,
              maxRetries: MAX_RETRIES,
              errorCode: fetchErr.code || null,
              errorMessage: fetchErr.message || null,
            });
            trackTelemetry('SutterSync.Task.Retry', {
              operation,
              endpoint,
              requestId,
              journeyId,
              taskRefId: payload.taskRefId || null,
              attempt,
              maxRetries: MAX_RETRIES,
              errorCode: fetchErr.code || null,
              errorMessage: fetchErr.message || null,
            });
            await sleep(1000 * attempt);
            continue;
          }
          throw fetchErr;
        }
      }

      console.log('[SutterSync][TaskRequest][SUCCESS]', {
        operation,
        endpoint,
        taskRefId: payload.taskRefId || null,
        requestId,
        journeyId,
        status: response.status
      });
      trackTelemetry('SutterSync.Task.Success', {
        operation,
        endpoint,
        requestId,
        journeyId,
        taskRefId: payload.taskRefId || null,
        status: response.status,
      });

      if (response.status === 200) {
        logTaskDebug('RESPONSE_SUMMARY', {
          operation,
          status: response.status,
          taskStatus: response.data?.taskStatus || null,
          hasContent: Boolean(response.data?.content),
          hasFormData: Boolean(response.data?.formData),
          hasPriorDecisions: Array.isArray(response.data?.priorDecisions) && response.data.priorDecisions.length > 0,
          flowSheetId: response.data?.flowSheetId || null,
        });
        setMessage(response.data.message || '');
        setReadOnly(
          response.data.taskStatus === 'ReadOnly' ||
            response.data.taskStatus === 'Completed'
        );

        if (response.data.content) {
          const contentSchema = JSON.parse(response.data.content.SchemaData);
          const resolvedContentId = response.data.contentId ?? null;
          console.log(`[SutterSync] op=${operation} | contentId=${resolvedContentId} | title="${contentSchema.title || ''}" | taskRefId=${payload.taskRefId || ''}`);
          console.log('[SutterSync] raw content keys:', Object.keys(response.data.content));
          setTitle(contentSchema.title  || '');
          setSections(contentSchema.sections || []);
          setIsLongContent(contentSchema.isLongContent);
          setPriorDecisions(response.data.priorDecisions || []);
          setFlowSheetId(response.data.flowSheetId || null);
          setSourceContentId(resolvedContentId);
        }

        // Handle formData from response if it exists and is not empty
        if (response.data.formData && Object.keys(response.data.formData).length > 0) {
          // Clean the formData to ensure it's serializable and doesn't contain circular references
          const cleanFormData = {};
          Object.keys(response.data.formData).forEach(key => {
            const item = response.data.formData[key];
            if (item && typeof item === 'object' && item.value !== undefined) {
              // Handle array values (for input fields)
              let cleanValue = item.value;
              if (Array.isArray(item.value)) {
                cleanValue = item.value
                  .filter(opt => opt !== '' && opt !== null && opt !== undefined) // strip stale empty strings
                  .map(opt => {
                    if (typeof opt === 'object' && opt !== null) {
                      return {
                        id: opt.id,
                        label: opt.label,
                        value: opt.value,
                        inputType: opt.inputType,
                        min: opt.min,
                        max: opt.max,
                        required: opt.required,
                        placeholder: opt.placeholder
                      };
                    }
                    return opt;
                  });
              }
              
              cleanFormData[key] = {
                value: cleanValue,
                label: item.label || '',
                metric: item.metric || null,
                flowSheetId: item.flowSheetId || null
              };
            }
          });
          setFormData(cleanFormData);
        }

        if (actionType === 'ranking' && response.data.message?.toLowerCase().includes('rating submitted')) {
          setTimeout(() => {
            window.close();
          }, 2000);
        }
        if (operation === 'submit' && (actionType === 'close')) {
          setTimeout(() => {
            window.close();
          }, 2000);
        }
        if(response.data.hasAgreedToTerms === false && operation === "complete" && actionType == "onboarding" && response.data.isHTNOnboarding){
          setTimeout(() => {
            window.close();
          }, 2000);
        }
        if(response.data.hasInsulinPump && operation === "complete" && actionType == "onboarding"){
          setTimeout(() => {
            window.close();
          }, 2000);
        }
        if (operation === 'finish' && actionType === 'close') {
          window.close();
        } else if(response.data.metadata?.operationType === "finish"){
          setTimeout(() => {
            window.close();
          }, 2000);
        }
      }
    } catch (error) {
      const isNetworkFailure = isNetworkError(error);
      console.error('[SutterSync][TaskRequest][ERROR]', {
        operation,
        endpoint,
        taskRefId: payload.taskRefId || null,
        status: error.response?.status || null,
        code: error.code || null,
        message: error.message || null,
        isNetworkError: isNetworkFailure
      });
      logTaskDebug('ERROR_RESPONSE_BODY', {
        operation,
        taskRefId: payload.taskRefId || null,
        responseMessage: error.response?.data?.message || null,
        responseKeys: error.response?.data ? Object.keys(error.response.data) : [],
      });
      setMessage(`Failed to ${operation} the task.`);
      console.error(`Error during ${operation}:`, error);
      setError(
        isNetworkFailure
          ? 'A network error occurred. Please try again.'
          : error.response?.data?.message || 'An unexpected error occurred.'
      );
      // Temporary debugging aid (SHOW-ERROR-DETAILS-ENABLED) — full picture for the live network/handshake investigation.
      const debugErrorDetails = {
        operation,
        endpoint,
        requestId,
        journeyId,
        taskRefId: payload.taskRefId || null,
        timestamp: new Date().toISOString(),

        // What we sent
        requestMethod: error.config?.method || 'post',
        requestUrl: error.config?.url || endpoint,
        requestHeaders: error.config?.headers || null,
        requestPayload: payload,

        // What came back (or why nothing came back)
        errorName: error.name || null,
        errorCode: error.code || null,
        message: error.message || null,
        isNetworkError: isNetworkFailure,
        isTimeout: error.code === 'ECONNABORTED',
        status: error.response?.status ?? null,
        statusText: error.response?.statusText ?? null,
        responseHeaders: error.response?.headers || null,
        responseData: error.response?.data ?? null,
        responseDataRaw: (() => {
          try {
            return typeof error.response?.data === 'string' ? error.response.data : JSON.stringify(error.response?.data);
          } catch (_stringifyError) {
            return null;
          }
        })(),

        // Full raw error for anything the fields above don't capture
        stack: error.stack || null,
        toJSON: (typeof error.toJSON === 'function') ? error.toJSON() : null,

        // Context that helps tell apart handshake/network/WAF/ingress failures
        ...getTokenDiagnostics(authToken),
        ...getLaunchPropertiesFromLocation(),
        pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      };
      setErrorDetails(debugErrorDetails);

      // Also ship the full dump to the UI container's App Insights so it's queryable without
      // reproducing the failure on-screen. Gated by the same flag — requestPayload/responseData
      // can carry PHI, so this must stay off outside active debugging.
      if (isErrorDetailsEnabled()) {
        trackTelemetry('SutterSync.Task.Error', {
          operation,
          endpoint,
          requestId,
          journeyId,
          taskRefId: payload.taskRefId || null,
          status: debugErrorDetails.status,
          errorCode: debugErrorDetails.errorCode,
          errorName: debugErrorDetails.errorName,
          message: debugErrorDetails.message,
          isNetworkError: debugErrorDetails.isNetworkError,
          isTimeout: debugErrorDetails.isTimeout,
          errorDump: JSON.stringify(debugErrorDetails),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async (params) => handleTaskOperation('progress', params);
  const handleComplete = async (params) =>
    handleTaskOperation('complete', params);
  const handleFinish = async (params) => handleTaskOperation('finish', params);
  const handleBack = async (params) => handleTaskOperation('back', params);
  const handleSubmit = async (params) => handleTaskOperation('submit', params);
  const handleSummary = async (params) => handleTaskOperation('summary', params);

  useEffect(() => {
    const autoStartTask = async () => {
      // In local mode (no API URL), auto-start with first screen without needing authToken
      if (!url) {
        if (hasStarted || (sections && sections.length > 0)) {
          logTaskDebug('AUTOSTART_SKIPPED_LOCAL', {
            reason: hasStarted ? 'already-started' : 'sections-already-loaded',
            sectionsCount: sections?.length || 0,
          });
          return;
        }
        logTaskDebug('AUTOSTART_LOCAL', {
          sectionsCount: sections?.length || 0,
        });
        setHasStarted(true);
        await handleTaskOperation('start', {});
        return;
      }

      if (!authToken?.TASKREFID) {
        logTaskDebug('AUTOSTART_SKIPPED', {
          reason: 'missing-taskrefid',
          hasAuthToken: Boolean(authToken),
        });
        return;
      }

      // Check if we already have content loaded (prevents duplicate calls on refresh)
      if (sections && sections.length > 0) {
        logTaskDebug('AUTOSTART_SKIPPED', {
          reason: 'sections-already-loaded',
          sectionsCount: sections.length,
        });
        return;
      }

      if (hasStarted) {
        logTaskDebug('AUTOSTART_SKIPPED', {
          reason: 'already-started',
        });
        return;
      }

      logTaskDebug('AUTOSTART_TRIGGERED', {
        taskRefId: authToken.TASKREFID,
        patId: authToken.PATID || null,
      });
      setHasStarted(true);
      await handleTaskOperation('start', {
        taskRefId: authToken.TASKREFID,
        patId: authToken.PATID,
        fhirPatId: authToken.FHIRPATID,
        taskInst: authToken.taskInst,
        epicTaskId: authToken.taskID,
        accessToken: authToken.access_token,
      });
    };

    if ((authToken || !url) && !hasStarted && (!sections || sections.length === 0)) {
      autoStartTask();
    }
  }, [authToken, hasStarted, sections]);

  return {
    loading,
    message,
    error,
    errorDetails,
    title,
    sections,
    isLongContent,
    formData,
    setFormData,
    priorDecisions,
    readOnly,
    sourceContentId,
    handleNext,
    handleComplete,
    handleFinish,
    handleBack,
    handleSubmit,
    handleSummary,
    updateFormData,
  };
};

export default useTask;
